import * as Sentry from "@sentry/react";
import type { CaptureContext, Extras } from "@sentry/types";

import { notify } from "./notifications";

export class CapturedError extends Error {
  constructor(
    public readonly name: string,
    public readonly message: string,
    public readonly source: string,
    public readonly originalError: unknown
  ) {
    super(message);
    if (originalError instanceof Error) {
      this.stack = originalError.stack;
    }
  }
}

const extractErrorMessage = (err: unknown): string => {
  if (!err) {
    return "empty error";
  }
  const errObj = err as Record<string, unknown>;
  const message = errObj.message;
  if (!message) {
    return "empty error message";
  }
  if (typeof message === "string") {
    return message;
  }
  if (typeof message === "object") {
    return message?.toString() ?? "unknown message";
  }
  return "no message could be extracted";
};

export const describeRPCError = (msg: string): string => {
  try {
    const result = JSON.parse(msg.substring("503 : ".length)) as {
      error: {
        code: string;
        message: string;
      };
    };
    return `${result.error.message} (${result.error.code})`;
  } catch (e) {
    // ignore parse error
  }
  return msg;
};

/**
 * Captures an exception.
 */
export const handleException = (
  err: unknown,
  {
    name = err instanceof Error ? err.name : "CapturedError",
    source = name ?? "unspecified",
    userMessage,
    groupInSentry,
    tags = {},
    extra,
  }: {
    /**
     * Custom name to apply to the error.
     */
    name?: string;
    /**
     * Source to apply to the error.
     */
    source?: string;
    /**
     * Notification to send to the user.
     */
    userMessage?: {
      title: string;
      /**
       * Defaults to error's message.
       */
      description?: string;
    };
    /**
     * If true, applies a fingerprint to group the errors by source and name.
     */
    groupInSentry?: boolean;
    tags?: Record<string, string>;
    extra?: Extras;
  }
): void => {
  const captured = new CapturedError(
    name,
    extractErrorMessage(err),
    source,
    err
  );

  console.error(`[${captured.name}] (from ${captured.source})`);
  console.error(captured);
  console.error(captured.originalError);

  notify({
    message: userMessage?.title ?? name ?? captured.name,
    description: userMessage?.description ?? captured.message,
    type: "error",
  });

  const sentryArgs: CaptureContext = {
    tags: {
      ...tags,
      source,
    },
    extra: {
      originalError: captured.originalError,
      userMessage,
      originalStack:
        captured.originalError instanceof Error
          ? captured.originalError.stack
          : undefined,
      ...extra,
    },
  };
  if (groupInSentry) {
    sentryArgs.fingerprint = [captured.name, source];
  }
  Sentry.captureException(captured, sentryArgs);
};
